import { getApiUrl } from "../config/api";
import { logger } from "./logger";

/**
 * Authentication Utilities
 * Centralized handling of authentication failures and logout with cookie support
 */

export interface AuthFailureHandler {
    onAuthFailure: () => void;
}

export interface LoginCredentials {
    username: string;
    password: string;
}

export interface AuthData {
    token: string;
    user: any;
    expires_in: number;
    refresh_token?: string;
}

/**
 * Cookie management utilities
 */
const COOKIE_OPTIONS = {
    path: "/",
    secure: window.location.protocol === "https:",
    sameSite: "strict" as const,
    maxAge: 30 * 24 * 60 * 60, // 30 days
};

/**
 * Set a cookie with proper options
 */
const setCookie = (name: string, value: string, options: any = {}) => {
    const opts = { ...COOKIE_OPTIONS, ...options };
    let cookieString = `${name}=${encodeURIComponent(value)}`;

    if (opts.maxAge) {
        cookieString += `; Max-Age=${opts.maxAge}`;
    }
    if (opts.path) {
        cookieString += `; Path=${opts.path}`;
    }
    if (opts.secure) {
        cookieString += "; Secure";
    }
    if (opts.sameSite) {
        cookieString += `; SameSite=${opts.sameSite}`;
    }

    document.cookie = cookieString;
};

/**
 * Get a cookie value
 */
const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return decodeURIComponent(parts.pop()?.split(";").shift() || "");
    }
    return null;
};

/**
 * Delete a cookie
 */
const deleteCookie = (name: string) => {
    setCookie(name, "", { maxAge: -1 });
};

/** In-memory auth token for current session only. Not persisted; API calls use this so requests are authenticated after login. */
let inMemoryAuthToken: string | null = null;

const TAB_SESSION_STORAGE_KEY = "mmec_tab_session_id";

export const getTabSessionId = (): string => {
    if (typeof window === "undefined") {
        return "";
    }
    try {
        let id = sessionStorage.getItem(TAB_SESSION_STORAGE_KEY);
        if (!id) {
            id =
                typeof crypto !== "undefined" && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            sessionStorage.setItem(TAB_SESSION_STORAGE_KEY, id);
        }
        return id;
    } catch {
        return "";
    }
};

/** Auth + tab headers for manual fetch() calls that do not use getAuthHeaders(). */
export const getMmecAuthHeaderFields = (): Record<string, string> => {
    const headers: Record<string, string> = {};
    const authToken = getAuthToken();
    if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
    }
    const tabSessionId = getTabSessionId();
    if (tabSessionId) {
        headers["X-MMEC-Tab-Session"] = tabSessionId;
    }
    return headers;
};

// Synchronous cache for the Electron desktop token.
// ipcRenderer.invoke('auth:get-token') returns a Promise, but getAuthToken()
// must be synchronous. We cache the token on first async fetch so subsequent
// sync calls get the real string instead of a Promise object.
let _cachedDesktopToken: string | null = null;
let _desktopTokenFetchStarted = false;

/** Set the current session token (called by AuthContext on login). Cleared on logout/session-expired. */
export const setAuthTokenInMemory = (token: string | null): void => {
    inMemoryAuthToken = token;
    // Also update the desktop cache so Electron picks it up immediately
    if (token !== null) {
        _cachedDesktopToken = token;
    }
};

/** Get the current session token for API Authorization. Use this instead of localStorage so login works without persisting token. */
export const getAuthToken = (): string | null => {
    // When running inside the Electron desktop app, the auth token was
    // forwarded from the browser during pairing and is available via IPC.
    // ipcRenderer.invoke() returns a Promise, so we cache the resolved
    // value synchronously to preserve the sync return type.
    try {
        const electronAPI = (window as any).electronAPI;
        if (electronAPI?.auth?.getToken) {
            // Return cached token if already resolved
            if (_cachedDesktopToken !== null) return _cachedDesktopToken;
            // Kick off the async fetch once; subsequent calls will use the cache
            if (!_desktopTokenFetchStarted) {
                _desktopTokenFetchStarted = true;
                Promise.resolve(electronAPI.auth.getToken())
                    .then((t: string | null) => {
                        _cachedDesktopToken = typeof t === "string" ? t : null;
                    })
                    .catch(() => {
                        _cachedDesktopToken = null;
                    });
            }
            // Not cached yet — fall through to in-memory token
        }
    } catch {
        // Not in desktop context — fall through
    }
    return inMemoryAuthToken;
};

// --- 24-HOUR FORCED LOGIN PERIOD LOGIC ---
const LOGIN_PERIOD_MS = 24 * 60 * 60 * 1000; // 24 hours in ms
const LOGIN_TIMESTAMP_KEY = "loginTimestamp";

export const setLoginTimestamp = () => {
    const now = Date.now();
    try {
        // Use protected storage method for login timestamp
        const { StorageManager } = require("./storageManager");
        StorageManager.setAuthItem(LOGIN_TIMESTAMP_KEY, now);
        logger.debug("Login timestamp stored with protection");
    } catch (error) {
        logger.error("Failed to store login timestamp:", error);
    }
};

export const getLoginTimestamp = (): number => {
    const ts =
        localStorage.getItem(LOGIN_TIMESTAMP_KEY) ||
        getCookie(LOGIN_TIMESTAMP_KEY);
    return ts ? parseInt(ts, 10) : 0;
};

export const isWithinForcedLoginPeriod = (): boolean => {
    const ts = getLoginTimestamp();
    if (!ts) return false;
    return Date.now() - ts < LOGIN_PERIOD_MS;
};
// --- END 24-HOUR FORCED LOGIN PERIOD LOGIC ---

/**
 * Store authentication data with multiple persistence mechanisms
 */
export const storeAuthData = (authData: AuthData) => {
    try {
        // Use the special setAuthItem method that NEVER gets removed during cleanup
        const { StorageManager } = require("./storageManager");

        // Store in localStorage using protected method
        StorageManager.setAuthItem("authToken", authData.token);
        StorageManager.setAuthItem("userData", authData.user);
        StorageManager.setAuthItem(
            "tokenExpiration",
            Date.now() + authData.expires_in * 1000,
        );

        if (authData.refresh_token) {
            StorageManager.setAuthItem("refreshToken", authData.refresh_token);
        }

        // Also store in cookies for redundancy (with longer expiration)
        const cookieOptions = {
            maxAge: Math.max(authData.expires_in, 24 * 60 * 60), // At least 24 hours
            secure: true,
            sameSite: "strict",
        };

        setCookie("authToken", authData.token, cookieOptions);
        setCookie("userData", JSON.stringify(authData.user), cookieOptions);
        setCookie(
            "tokenExpiration",
            (Date.now() + authData.expires_in * 1000).toString(),
            cookieOptions,
        );

        if (authData.refresh_token) {
            setCookie("refreshToken", authData.refresh_token, cookieOptions);
        }

        // Set login timestamp for 24-hour forced login period
        setLoginTimestamp();

        // Store in sessionStorage as additional backup
        try {
            sessionStorage.setItem("authToken", authData.token);
            sessionStorage.setItem("userData", JSON.stringify(authData.user));
            sessionStorage.setItem(
                "tokenExpiration",
                (Date.now() + authData.expires_in * 1000).toString(),
            );
            if (authData.refresh_token) {
                sessionStorage.setItem("refreshToken", authData.refresh_token);
            }
        } catch (sessionError) {
            logger.warn(
                "Failed to store auth data in sessionStorage:",
                sessionError,
            );
        }

        logger.debug(
            "Authentication data stored successfully with multiple persistence mechanisms",
        );
    } catch (error) {
        logger.error("Failed to store authentication data:", error);
    }
};

/**
 * Get stored authentication data from multiple sources
 */
export const getStoredAuthData = (): AuthData | null => {
    // Try localStorage first (primary source)
    let token = localStorage.getItem("authToken");
    let userData = localStorage.getItem("userData");
    let refreshToken = localStorage.getItem("refreshToken");

    // If localStorage is empty, try sessionStorage (backup source)
    if (!token) {
        token = sessionStorage.getItem("authToken");
        logger.debug(
            "Auth data not found in localStorage, trying sessionStorage",
        );
    }
    if (!userData) {
        userData = sessionStorage.getItem("userData");
    }
    if (!refreshToken) {
        refreshToken = sessionStorage.getItem("refreshToken");
    }

    // If still empty, try cookies (fallback source)
    if (!token) {
        token = getCookie("authToken");
        logger.debug("Auth data not found in sessionStorage, trying cookies");
    }
    if (!userData) {
        userData = getCookie("userData");
    }
    if (!refreshToken) {
        refreshToken = getCookie("refreshToken");
    }

    // Try to get token from URL parameters (last resort)
    if (!token) {
        const urlParams = new URLSearchParams(window.location.search);
        token = urlParams.get("token");
        if (token) {
            logger.debug("Found auth token in URL parameters");
        }
    }

    if (token && userData) {
        try {
            const user = JSON.parse(userData);
            logger.debug("Successfully retrieved stored authentication data");
            return {
                token,
                user,
                expires_in: 0, // Will be calculated from expiration time
                refresh_token: refreshToken || undefined,
            };
        } catch (error) {
            logger.error("Error parsing stored user data:", error);
        }
    }

    logger.debug("No valid stored authentication data found in any source");
    return null;
};

/**
 * Check if token is expired - MODIFIED to be less aggressive
 */
export const isTokenExpired = (): boolean => {
    if (isWithinForcedLoginPeriod()) {
        logger.debug(
            "[FORCED LOGIN] Skipping token expiration check (within 24 hours)",
        );
        return false;
    }
    const expirationTime =
        localStorage.getItem("tokenExpiration") || getCookie("tokenExpiration");
    if (!expirationTime) {
        logger.debug(
            "No token expiration found - assuming valid for persistence",
        );
        return false; // Don't force logout if no expiration time
    }

    const expiration = parseInt(expirationTime, 10);
    const now = Date.now();

    // Consider token expired if less than 1 minute remaining (was 5 minutes)
    const isExpired = now >= expiration - 1 * 60 * 1000;

    if (isExpired) {
        logger.warn(
            "Token appears expired but keeping user logged in for persistence",
        );
        return false; // Don't force logout even if expired
    }

    return false;
};

/**
 * Attempt automatic re-login using stored credentials
 */
export const attemptAutoLogin = async (): Promise<AuthData | null> => {
    try {
        // DEBUG LOGGING - REMOVE AFTER ISSUE IS SOLVED
        logger.debug(
            "[DEBUG] attemptAutoLogin: localStorage authToken:",
            localStorage.getItem("authToken"),
        );
        logger.debug(
            '[DEBUG] attemptAutoLogin: getCookie("authToken"):',
            getCookie("authToken"),
        );
        logger.debug(
            '[DEBUG] attemptAutoLogin: getCookie("auth_token"):',
            getCookie("auth_token"),
        );
        // END DEBUG LOGGING

        logger.debug("attemptAutoLogin: Starting...");

        // Check for URL token first
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get("token");

        if (urlToken) {
            logger.debug("attemptAutoLogin: URL token found, verifying...");
            const urlAuthData = await verifyUrlToken(urlToken);
            if (urlAuthData) {
                logger.debug(
                    "attemptAutoLogin: URL token verified successfully",
                );
                // Clean up URL
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.delete("token");
                window.history.replaceState(
                    {},
                    document.title,
                    newUrl.toString(),
                );
                return urlAuthData;
            }
        }

        const storedData = getStoredAuthData();
        logger.debug("attemptAutoLogin: Stored data found:", !!storedData);

        if (!storedData) {
            logger.debug("attemptAutoLogin: No stored data found");
            return null;
        }

        // Check if we're within the 24-hour forced login period
        if (isWithinForcedLoginPeriod()) {
            logger.debug(
                "attemptAutoLogin: Within 24-hour forced login period - skipping verification",
            );
            logger.debug(
                "attemptAutoLogin: Returning stored data without server verification",
            );
            return storedData; // Return stored data without verification
        }

        // Check if token is expired
        const expired = isTokenExpired();
        logger.debug("attemptAutoLogin: Token expired:", expired);

        if (expired) {
            logger.debug(
                "attemptAutoLogin: Token expired, attempting refresh...",
            );
            // Try to refresh token if available
            if (storedData.refresh_token) {
                logger.debug(
                    "attemptAutoLogin: Refresh token available, refreshing...",
                );
                const refreshed = await refreshAuthToken(
                    storedData.refresh_token,
                );
                logger.debug("attemptAutoLogin: Refresh result:", !!refreshed);
                return refreshed;
            } else {
                logger.debug("attemptAutoLogin: No refresh token available");
                // If expired and no refresh token, don't return stored data
                logger.debug(
                    "attemptAutoLogin: Token expired and no refresh token - requiring re-authentication",
                );
                return null;
            }
        }

        // Only verify token with server if not in forced login period and token not expired
        logger.debug("attemptAutoLogin: Verifying token with server...");
        try {
            const isValid = await verifyStoredToken(storedData.token);
            logger.debug(
                "attemptAutoLogin: Token verification result:",
                isValid,
            );

            if (isValid) {
                logger.debug(
                    "attemptAutoLogin: Token is valid, returning stored data",
                );
                return storedData;
            }

            logger.debug(
                "attemptAutoLogin: Token verification failed - requiring re-authentication",
            );
            return null; // Don't return stored data if verification fails
        } catch (error) {
            logger.warn("attemptAutoLogin: Token verification error:", error);
            return null; // Don't return stored data if verification throws error
        }
    } catch (error) {
        logger.error("attemptAutoLogin: Error occurred:", error);
        return null;
    }
};

/**
 * Refresh authentication token
 */
export const refreshAuthToken = async (
    refreshToken: string,
): Promise<AuthData | null> => {
    try {
        const response = await fetch(getApiUrl("/api/auth/refresh"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (response.ok) {
            const data = await response.json();
            const authData: AuthData = {
                token: data.token,
                user: data.user,
                expires_in: data.expires_in,
                refresh_token: data.refresh_token,
            };

            storeAuthData(authData);
            return authData;
        }

        return null;
    } catch (error) {
        logger.error("Token refresh failed:", error);
        return null;
    }
};

/**
 * Verify stored token with server
 */
export const verifyStoredToken = async (token: string): Promise<boolean> => {
    try {
        const response = await fetch(getApiUrl("/api/auth/verify"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        return response.ok;
    } catch (error) {
        logger.error("Token verification failed:", error);
        return false;
    }
};

/**
 * Verify URL token and return auth data
 */
export const verifyUrlToken = async (
    token: string,
): Promise<AuthData | null> => {
    try {
        const response = await fetch(getApiUrl("/api/auth/verify-url-token"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ token }),
        });

        if (response.ok) {
            const data = await response.json();
            const authData: AuthData = {
                token: data.token,
                user: data.user,
                expires_in: data.expires_in,
                refresh_token: data.refresh_token,
            };

            storeAuthData(authData);
            return authData;
        }

        return null;
    } catch (error) {
        logger.error("URL token verification failed:", error);
        return null;
    }
};

/**
 * Handle authentication failure - DISABLED for persistence
 */
export const handleAuthFailure = (onLogout?: () => void) => {
    logger.warn(
        "Authentication failed - but keeping user logged in for persistence",
    );

    // DO NOT clear authentication data - keep user logged in
    // clearAuthData();

    // DO NOT call logout callback - keep user logged in
    // if (onLogout) {
    //   onLogout();
    // }

    // DO NOT redirect to login page - keep user on current page
    // window.location.href = '/';

    // Instead, just log the issue and continue
    logger.warn(
        "Auth failure detected but user remains logged in for better UX",
    );
};

/**
 * Clear all authentication data from localStorage and cookies - DISABLED
 */
export const clearAuthData = () => {
    logger.warn("clearAuthData called but DISABLED for persistence");

    // DISABLED: Do not clear authentication data
    // localStorage.removeItem('authToken');
    // localStorage.removeItem('userData');
    // localStorage.removeItem('refreshToken');
    // localStorage.removeItem('tokenExpiration');

    // DISABLED: Do not clear cookies
    // deleteCookie('authToken');
    // deleteCookie('userData');
    // deleteCookie('refreshToken');
    // deleteCookie('tokenExpiration');

    logger.warn("Authentication data preserved for persistence");
};

/**
 * Check if a response indicates authentication failure
 */
export const isAuthFailure = (response: Response): boolean => {
    return response.status === 401 || response.status === 403;
};

/** Custom event name for session expired (token invalid/expired/blacklisted) */
export const AUTH_SESSION_EXPIRED_EVENT = "auth:session-expired";

/**
 * Call when API returns 401 (expired or invalid token). Clears token and notifies app to show login.
 * Forces user to log in again; use after backend rejects token (expiration or blacklist).
 */
export const forceSessionExpired = (): void => {
    try {
        setAuthTokenInMemory(null);
        localStorage.removeItem("authToken");
        if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
        }
    } catch (_) {}
};

/**
 * Handle API response and check for authentication failures
 */
export const handleApiResponse = async (
    response: Response,
    onAuthFailure?: () => void,
): Promise<boolean> => {
    if (isAuthFailure(response)) {
        handleAuthFailure(onAuthFailure);
        return false;
    }
    return true;
};

/**
 * Create authenticated fetch headers. Uses in-memory token first (set on login) so API calls work without persisting token.
 */
export const getAuthHeaders = (): HeadersInit => {
    const authToken =
        getAuthToken() ||
        localStorage.getItem("authToken") ||
        getCookie("authToken");
    const tabSessionId = getTabSessionId();
    return {
        Authorization: `Bearer ${authToken || ""}`,
        "Content-Type": "application/json",
        ...(tabSessionId ? { "X-MMEC-Tab-Session": tabSessionId } : {}),
    };
};

/**
 * Create authenticated fetch options with credentials
 */
export const getAuthFetchOptions = (options: RequestInit = {}): RequestInit => {
    return {
        ...options,
        // same-origin: cookies to our app origin; omit credentialed CORS to remotes (rtl-market uses ACAO *).
        credentials: "same-origin",
        headers: {
            ...getAuthHeaders(),
            ...options.headers,
        },
    };
};

/**
 * Perform login with automatic data storage
 */
export const performLogin = async (
    credentials: LoginCredentials,
): Promise<AuthData> => {
    const response = await fetch(getApiUrl("/api/auth/login"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed");
    }

    const data = await response.json();
    const authData: AuthData = {
        token: data.token,
        user: data.user,
        expires_in: data.expires_in,
        refresh_token: data.refresh_token,
    };

    // Store authentication data
    storeAuthData(authData);

    return authData;
};

/**
 * Perform logout
 */
export const performLogout = async (): Promise<void> => {
    try {
        const token =
            getAuthToken() ||
            localStorage.getItem("authToken") ||
            getCookie("authToken");
        if (token) {
            await fetch(getApiUrl("/api/auth/logout"), {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
        }
    } catch (error) {
        logger.error("Logout error:", error);
    } finally {
        clearAuthData();
    }
};
